import React, { useState, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Store, Book, Users, Package, DollarSign, Award, Megaphone } from 'lucide-react';
import styled from 'styled-components';
import { ThemeColors, Spacing, Typography } from '../../styles/ThemeStyles';
import { PageContainer, Grid } from '../../styles/StyledComponents';
import { DashboardCard } from '../../components/shared/DashboardCard';
import { useAnnouncements } from '../../hooks/useAnnouncements';
import { AnnouncementCard } from '../../components/announcements/AnnouncementCard';

const MainContent = styled.main`
  padding: ${Spacing.lg} ${Spacing.md};
  padding-top: calc(${Spacing.lg} + 80px); /* Account for fixed header */
`;

const WelcomeHeading = styled.h1`
  font-family: 'Roboto', sans-serif;
  font-size: 2rem;
  font-weight: 500;
  color: ${ThemeColors.secondary.main};
  text-align: left;
  margin-bottom: ${Spacing.xl};
  padding-left: 0px;
  margin-top: -70px;
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${Spacing.md};
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const SectionTitle = styled.h2`
  font-size: ${Typography.fontSize.lg};
  font-weight: ${Typography.fontWeight.bold};
  color: ${ThemeColors.text.primary};
  margin-bottom: ${Spacing.md};
  margin-top: ${Spacing.lg};
`;

const QuickActionButton = styled.button`
  background: white;
  border: 2px solid ${ThemeColors.border.light};
  border-radius: 12px;
  padding: ${Spacing.sm} ${Spacing.md};
  cursor: pointer;
  transition: all 0.3s ease;
  color: ${ThemeColors.primary.main};
  font-weight: ${Typography.fontWeight.medium};
  font-size: ${Typography.fontSize.sm};
  width: 100%;

  &:hover {
    border-color: ${ThemeColors.primary.main};
    background-color: ${ThemeColors.primary.light};
    transform: translateY(-2px);
  }
`;

export const SalesRepDashboardScreenMobile = React.memo(() => {
  const [isMenuCollapsed, setIsMenuCollapsed] = useState(true);
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { 
    announcements, 
    loading: isLoadingAnnouncements 
  } = useAnnouncements({ status: 'ACTIVE' });

  const menuItems = useMemo(() => [
    {
      id: 'store-visit',
      label: t('navigation.storeVisit', 'Store Visit'),
      icon: Store,
      description: t('dashboard.storeVisitDesc', 'Visit stores, manage information, and track customer interactions.'),
      action: () => navigate('/store-visit')
    },
    {
      id: 'store-resource',
      label: t('navigation.storeResource', 'Store Resource'),
      icon: Book,
      description: t('dashboard.storeResourceDesc', 'Access marketing materials, product information, and sales resources.'),
      action: () => navigate('/store-resource')
    },
    {
      id: 'payroll',
      label: t('navigation.payroll', 'Payroll'),
      icon: DollarSign,
      description: t('dashboard.payrollDesc', 'View commission reports, payment history, and expense tracking.'),
      action: () => navigate('/payroll')
    },
    {
      id: 'contest-entry',
      label: t('navigation.contestEntry', 'Contest Entry'),
      icon: Award,
      description: t('dashboard.contestEntryDesc', 'Participate in sales contests and view performance rankings.'),
      action: () => navigate('/contest-entry')
    }
  ], [t, navigate]);

  return (
    <PageContainer isMenuCollapsed={isMenuCollapsed}>
      <MainContent>
        <WelcomeHeading>
          {t('dashboard.welcomeBack', 'Welcome Back')} {user?.firstName || user?.username || ''}
        </WelcomeHeading>
        
        {/* Announcements Section - Moved to top after welcome back */}
        {announcements.length > 0 && (
          <>
            <SectionTitle>
              <Megaphone style={{ display: 'inline', marginRight: Spacing.xs, verticalAlign: 'middle' }} size={18} />
              Recent Announcements
            </SectionTitle>
            <Grid minWidth="280px" gap={Spacing.md}>
              {announcements.slice(0, 3).map((announcement) => (
                <AnnouncementCard
                  key={announcement.id}
                  announcement={announcement}
                  variant="mobile"
                  onView={(id) => navigate(`/announcements/${id}`)}
                />
              ))}
            </Grid>
            {announcements.length > 3 && (
              <div style={{ marginTop: Spacing.md }}>
                <QuickActionButton onClick={() => navigate('/announcements')}>
                  View All Announcements ({announcements.length})
                </QuickActionButton>
              </div>
            )}
          </>
        )}
        
        <CardGrid style={{ marginTop: announcements.length > 0 ? Spacing.xl : 0 }}>
          {menuItems.map((item) => (
            <DashboardCard
              key={item.id}
              id={item.id}
              label={item.label}
              description={item.description}
              icon={item.icon}
              onClick={item.action}
              variant="mobile"
            />
          ))}
        </CardGrid>
      </MainContent>
    </PageContainer>
  );
});

SalesRepDashboardScreenMobile.displayName = 'SalesRepDashboardScreenMobile';